(() => {
  if (window.__eraspaceSeamlessFeedLoaded) return;
  window.__eraspaceSeamlessFeedLoaded = true;

  const SELECTOR = '[data-eraspace-seamless-feed]';
  const TRACK_SELECTOR = 'collection-list, product-list';
  const ITEM_SELECTOR = 'product-card, .collection-card';
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DESKTOP_QUERY = window.matchMedia('(min-width: 700px)');
  const FRAME_INTERVAL_LABEL = 'native-rAF';
  const DESKTOP_AUTO_SPEED = 74.4 / 1000; // 60% of the previous desktop auto-scroll speed
  const MOBILE_AUTO_SPEED = 34.8 / 1000; // 60% of the previous mobile auto-scroll speed
  const DESKTOP_DRAG_MULTIPLIER = 1;
  const DESKTOP_WHEEL_MULTIPLIER = 1.35;
  const DESKTOP_DRAG_THRESHOLD = 8;
  const DESKTOP_DRAG_AXIS_RATIO = 1.25;
  const HOLD_DRAG_DELAY = 140;
  const HOLD_DRAG_THRESHOLD = 3;
  const CLICK_SLOP = 6;
  const MAX_RESUME_DELTA = 50; // Avoid huge jumps after tab/background pauses without slowing normal frames.
  const DRAG_THRESHOLD = 6;
  const INERTIA_MIN_VELOCITY = 0.018;
  const INERTIA_MAX_VELOCITY = 2.4;
  const DRAG_RELEASE_BOOST = 1.65;
  const DRAG_INERTIA_DECAY = 0.955;
  const DRAG_INERTIA_STALE_MS = 140;
  const WHEEL_INERTIA_DECAY = 0.92;
  const WHEEL_INERTIA_DELAY = 80;
  const INPUT_RESUME_DELAY = 1000;
  const INITIAL_RESUME_DELAY = 1000;
  const getAutoSpeed = () => DESKTOP_QUERY.matches ? DESKTOP_AUTO_SPEED : MOBILE_AUTO_SPEED;
  const getDragMultiplier = () => DESKTOP_QUERY.matches ? DESKTOP_DRAG_MULTIPLIER : 1;
  const getWheelMultiplier = () => DESKTOP_QUERY.matches ? DESKTOP_WHEEL_MULTIPLIER : 1;
  const getDragThreshold = () => DESKTOP_QUERY.matches ? DESKTOP_DRAG_THRESHOLD : DRAG_THRESHOLD;
  const getDragAxisRatio = () => DESKTOP_QUERY.matches ? DESKTOP_DRAG_AXIS_RATIO : 1;
  const states = new WeakMap();

  const removeCollectionCardOverlays = (el) => {
    el.querySelectorAll('.collection-card .collection-card__content-wrapper').forEach((overlay) => overlay.remove());
  };

  const ensureDragStyles = () => {
    if (document.getElementById('eraspace-seamless-feed-drag-style')) return;

    const style = document.createElement('style');
    style.id = 'eraspace-seamless-feed-drag-style';
    style.textContent = `
      [data-eraspace-seamless-feed] {
        touch-action: auto;
        will-change: scroll-position;
      }

      [data-eraspace-seamless-feed] collection-list,
      [data-eraspace-seamless-feed] product-list {
        transform: translate3d(0, 0, 0);
        backface-visibility: hidden;
        will-change: transform;
      }

      [data-eraspace-seamless-feed] img,
      [data-eraspace-seamless-feed] picture,
      [data-eraspace-seamless-feed] a {
        -webkit-user-drag: none;
        user-drag: none;
      }

      @media (hover: hover) and (pointer: fine) {
        [data-eraspace-seamless-feed].eraspace-feed--drag-ready { cursor: grab; }
        [data-eraspace-seamless-feed].eraspace-feed--dragging,
        [data-eraspace-seamless-feed].eraspace-feed--dragging * {
          cursor: grabbing !important;
          user-select: none;
          -webkit-user-select: none;
        }
      }
    `;
    document.head.appendChild(style);
  };

  const getTrack = (el) => el.querySelector(TRACK_SELECTOR);
  const getItems = (track) => track ? Array.from(track.children).filter((item) => item.matches(ITEM_SELECTOR)) : [];
  const toNumber = (value) => Number.parseFloat(value) || 0;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const clampVelocity = (velocity) => clamp(velocity, -INERTIA_MAX_VELOCITY, INERTIA_MAX_VELOCITY);

  const disableNativeDrag = (el) => {
    el.querySelectorAll('img, a').forEach((node) => {
      node.setAttribute('draggable', 'false');
    });
  };

  const getGap = (track) => {
    const styles = window.getComputedStyle(track);
    return toNumber(styles.columnGap || styles.gap);
  };

  const getAdvance = (track, item) => {
    const items = getItems(track);
    const index = items.indexOf(item);

    if (index !== -1 && items.length > 1) {
      const neighbor = items[index === 0 ? 1 : index - 1];
      const distance = Math.abs(neighbor.getBoundingClientRect().left - item.getBoundingClientRect().left);

      if (distance > 1) return distance;
    }

    return item.getBoundingClientRect().width + getGap(track);
  };

  const getFeedHeading = (el) => {
    const section = el.closest('[id^="shopify-section-"], .shopify-section');
    const heading = section?.querySelector('section-header h1, section-header h2, section-header h3, .section-header h1, .section-header h2, .section-header h3');
    return (heading?.textContent || '').trim().toLowerCase();
  };

  const getVisualDirection = (el) => {
    const heading = getFeedHeading(el);
    if (heading.includes('latest') || heading.includes('arrival')) return 1;
    return el.dataset.eraspaceFeedDirection === 'left' ? 1 : -1;
  };

  const setScrollLeft = (el, state, value, time = performance.now()) => {
    state.position = value;
    state.ignoreScrollUntil = time + 120;
    state.autoScrolling = true;
    el.scrollLeft = value;
    state.lastWrittenScrollLeft = el.scrollLeft;
    state.autoScrolling = false;
  };

  const pause = (el, duration = INPUT_RESUME_DELAY) => {
    const state = states.get(el);
    if (!state) return;

    state.pausedUntil = performance.now() + duration;
    state.lastFrame = 0;
  };

  const stopInertia = (state) => {
    if (!state) return;

    if (state.inertiaTimer) {
      window.clearTimeout(state.inertiaTimer);
      state.inertiaTimer = 0;
    }

    if (state.inertiaFrame) {
      window.cancelAnimationFrame(state.inertiaFrame);
      state.inertiaFrame = 0;
    }

    state.inertiaVelocity = 0;
    state.inertiaLastFrame = 0;
    state.inertiaSource = '';
  };

  const startInertia = (el, state, velocity, source = 'drag', delay = 0) => {
    if (REDUCED_MOTION.matches || !state || !state.track || state.track.scrollWidth <= el.clientWidth + 8) return;

    const initialVelocity = clampVelocity(velocity);
    if (Math.abs(initialVelocity) < INERTIA_MIN_VELOCITY) return;

    stopInertia(state);
    state.inertiaVelocity = initialVelocity;
    state.inertiaSource = source;
    state.inertiaLastFrame = 0;
    state.pausedUntil = performance.now() + INPUT_RESUME_DELAY;
    state.lastFrame = 0;

    const run = () => {
      const latest = states.get(el);
      if (!latest || latest.dragging || latest.dragCandidate || REDUCED_MOTION.matches || document.hidden || !latest.visible) {
        stopInertia(latest);
        return;
      }

      const step = (time) => {
        const current = states.get(el);
        if (!current || current.dragging || current.dragCandidate || REDUCED_MOTION.matches || document.hidden || !current.visible) {
          stopInertia(current);
          return;
        }

        if (!current.inertiaLastFrame) {
          current.inertiaLastFrame = time;
          current.inertiaFrame = window.requestAnimationFrame(step);
          return;
        }

        const delta = Math.min(MAX_RESUME_DELTA, Math.max(0, time - current.inertiaLastFrame));
        current.inertiaLastFrame = time;

        if (delta <= 0) {
          current.inertiaFrame = window.requestAnimationFrame(step);
          return;
        }

        const travel = current.inertiaVelocity * delta;
        const direction = current.inertiaVelocity > 0 ? 1 : -1;
        setScrollLeft(el, current, current.position + travel, time);
        normalizeDirection(el, current, time, direction);

        const decay = current.inertiaSource === 'wheel' ? WHEEL_INERTIA_DECAY : DRAG_INERTIA_DECAY;
        current.inertiaVelocity *= Math.pow(decay, delta / (1000 / 60));
        el.dataset.eraspaceInertia = current.inertiaSource;

        if (Math.abs(current.inertiaVelocity) < INERTIA_MIN_VELOCITY) {
          stopInertia(current);
          current.pausedUntil = Math.max(current.pausedUntil, time + INPUT_RESUME_DELAY);
          current.lastFrame = 0;
          return;
        }

        current.inertiaFrame = window.requestAnimationFrame(step);
      };

      latest.inertiaFrame = window.requestAnimationFrame(step);
    };

    if (delay > 0) {
      state.inertiaTimer = window.setTimeout(run, delay);
    } else {
      run();
    }
  };

  const pointerInsideRect = (state, rect) => (
    state.pointerX >= rect.left &&
    state.pointerX <= rect.right &&
    state.pointerY >= rect.top &&
    state.pointerY <= rect.bottom
  );

  const isSelectionTarget = (el, target) => {
    if (!(target instanceof Element)) return false;

    const item = target.closest(ITEM_SELECTOR);
    return !!item && el.contains(item);
  };

  const isStripHoverTarget = (el) => {
    const state = states.get(el);
    if (!state || !state.pointerInside || !state.track) return false;

    const feedRect = el.getBoundingClientRect();
    if (!pointerInsideRect(state, feedRect)) return false;

    const visibleItems = getItems(state.track).filter((item) => {
      const rect = item.getBoundingClientRect();
      return rect.right > feedRect.left && rect.left < feedRect.right && rect.bottom > feedRect.top && rect.top < feedRect.bottom;
    });

    if (!visibleItems.length) return false;

    const stripTop = Math.min(...visibleItems.map((item) => item.getBoundingClientRect().top));
    const stripBottom = Math.max(...visibleItems.map((item) => item.getBoundingClientRect().bottom));

    return state.pointerY >= stripTop && state.pointerY <= stripBottom;
  };

  const refreshHoverState = (el) => {
    const state = states.get(el);
    if (!state || !state.pointerInside) return false;

    const target = document.elementFromPoint(state.pointerX, state.pointerY);
    const overScrollableStrip = isSelectionTarget(el, target) || isStripHoverTarget(el);

    if (state.hoveredStrip !== overScrollableStrip) {
      state.hoveredStrip = overScrollableStrip;
      state.lastFrame = 0;
    }

    return overScrollableStrip;
  };

  const syncPointer = (el, event) => {
    const state = states.get(el);
    if (!state) return;

    state.pointerInside = true;
    state.pointerX = event.clientX;
    state.pointerY = event.clientY;
    refreshHoverState(el);
  };

  const releasePointer = (el, event) => {
    try {
      if (event.pointerId != null && el.hasPointerCapture?.(event.pointerId)) {
        el.releasePointerCapture(event.pointerId);
      }
    } catch (_) {
      // Ignore release errors from synthetic or already-released pointer events.
    }
  };

  const cancelDrag = (el, event) => {
    const state = states.get(el);
    if (!state) return;

    state.dragCandidate = false;
    state.dragging = false;
    state.dragPointerId = null;
    el.classList.remove('eraspace-feed--dragging');
    releasePointer(el, event);
  };

  const endDrag = (el, event) => {
    const state = states.get(el);
    if (!state) return;

    const wasDragging = state.dragging;
    const releaseTime = performance.now();
    const velocityAge = releaseTime - (state.dragLastTime || releaseTime);
    const freshRelease = clamp(1 - (velocityAge / DRAG_INERTIA_STALE_MS), 0, 1);
    const velocity = state.dragVelocity * DRAG_RELEASE_BOOST * freshRelease;
    cancelDrag(el, event);

    if (wasDragging) {
      pause(el, INPUT_RESUME_DELAY);
      startInertia(el, state, velocity, 'drag');
    }
  };

  const beginDrag = (el, event) => {
    const state = states.get(el);
    if (!state || event.button !== 0 || event.isPrimary === false) return;

    if (event.pointerType === 'touch') {
      state.dragCandidate = false;
      state.dragging = false;
      state.dragPointerId = null;
      state.dragPointerType = 'touch';
      state.blockClick = false;
      stopInertia(state);
      return;
    }

    state.pointerInside = true;
    state.pointerX = event.clientX;
    state.pointerY = event.clientY;
    state.blockClick = false;
    state.dragStartAt = performance.now();
    state.dragPointerType = event.pointerType || 'mouse';
    state.dragVelocity = 0;
    state.dragLastTime = state.dragStartAt;
    stopInertia(state);

    if (!isSelectionTarget(el, event.target) && !isStripHoverTarget(el)) return;

    state.dragCandidate = true;
    state.dragging = false;
    state.dragPointerId = event.pointerId;
    state.dragStartX = event.clientX;
    state.dragStartY = event.clientY;
    state.dragLastX = event.clientX;
    state.lastFrame = 0;

    pause(el, INPUT_RESUME_DELAY);
  };

  const handleDragMove = (el, event) => {
    const state = states.get(el);
    if (!state || (!state.dragCandidate && !state.dragging) || state.dragPointerId !== event.pointerId || event.pointerType === 'touch') return;

    syncPointer(el, event);

    const time = performance.now();
    const isTouch = event.pointerType === 'touch';
    const totalX = event.clientX - state.dragStartX;
    const totalY = event.clientY - state.dragStartY;
    const absX = Math.abs(totalX);
    const absY = Math.abs(totalY);

    if (!state.dragging) {
      const threshold = isTouch ? 12 : getDragThreshold();
      const axisRatio = isTouch ? 1.15 : getDragAxisRatio();
      const elapsed = time - state.dragStartAt;
      const relaxedThreshold = elapsed >= HOLD_DRAG_DELAY ? (isTouch ? 8 : HOLD_DRAG_THRESHOLD) : threshold;
      const horizontalEnough = absX >= relaxedThreshold && absX >= absY * axisRatio;
      const verticalEnough = absY >= threshold && absY > absX * axisRatio;

      if (horizontalEnough) {
        state.dragging = true;
        state.blockClick = true;
        el.classList.add('eraspace-feed--dragging');

        try {
          el.setPointerCapture?.(event.pointerId);
        } catch (_) {
          // Pointer capture is progressive enhancement; dragging still works without it.
        }
      } else {
        if (isTouch && verticalEnough) {
          cancelDrag(el, event);
          return;
        }

        const clickSlop = elapsed >= HOLD_DRAG_DELAY ? 2 : CLICK_SLOP;
        if (!isTouch && Math.max(absX, absY) >= clickSlop) {
          state.blockClick = true;
          event.preventDefault();
        }

        return;
      }
    }

    event.preventDefault();

    const deltaTime = Math.max(8, time - (state.dragLastTime || state.dragStartAt || time - 16));
    const deltaX = (event.clientX - state.dragLastX) * getDragMultiplier();
    state.dragLastX = event.clientX;
    state.dragLastTime = time;

    if (Math.abs(deltaX) < 0.1) return;

    const scrollDelta = -deltaX;
    const velocity = scrollDelta / deltaTime;
    state.dragVelocity = clampVelocity(state.dragVelocity ? (state.dragVelocity * 0.35) + (velocity * 0.65) : velocity);

    const direction = scrollDelta > 0 ? 1 : -1;
    setScrollLeft(el, state, state.position + scrollDelta, time);
    normalizeDirection(el, state, time, direction);
  };

  const normalizeForward = (el, state, time) => {
    let guard = 0;
    let first = getItems(state.track)[0];

    while (first && state.position >= getAdvance(state.track, first) && guard < 100) {
      const advance = getAdvance(state.track, first);
      state.track.appendChild(first);
      setScrollLeft(el, state, state.position - advance, time);
      first = getItems(state.track)[0];
      guard += 1;
    }
  };

  const normalizeBackward = (el, state, time) => {
    let guard = 0;

    while (guard < 100) {
      const items = getItems(state.track);
      const first = items[0];
      const last = items[items.length - 1];

      if (!first || !last || first === last) return;

      const advance = getAdvance(state.track, last);
      const buffer = advance * 1.5;

      // Keep right-moving feeds away from the physical scrollLeft=0 edge.
      // Rotating before the edge removes the visible hitch that happens when the browser clamps at 0.
      if (state.position > buffer) return;

      state.track.insertBefore(last, first);
      setScrollLeft(el, state, state.position + advance, time);
      guard += 1;
    }
  };

  const normalizeDirection = (el, state, time, direction) => {
    if (!state.track) return;

    if (direction > 0) {
      normalizeForward(el, state, time);
    } else if (direction < 0) {
      normalizeBackward(el, state, time);
    }
  };

  const normalizeLoop = (el, state, time) => normalizeDirection(el, state, time, state.direction);

  const start = (el) => {
    const state = states.get(el);
    if (!state || state.frame) return;

    const step = (time) => {
      const latest = states.get(el);
      if (!latest) return;

      latest.frame = window.requestAnimationFrame(step);
      refreshHoverState(el);

      if (REDUCED_MOTION.matches || !latest.visible || document.hidden || latest.dragging || latest.inertiaFrame || latest.inertiaTimer || latest.hoveredStrip || latest.focusedSelection || time < latest.pausedUntil) {
        latest.lastFrame = 0;
        return;
      }

      if (!latest.track || latest.track.scrollWidth <= el.clientWidth + 8) {
        latest.lastFrame = 0;
        return;
      }

      if (!latest.lastFrame) {
        latest.lastFrame = time;
        normalizeLoop(el, latest, time);
        return;
      }

      const delta = Math.min(MAX_RESUME_DELTA, Math.max(0, time - latest.lastFrame));
      latest.lastFrame = time;

      if (delta <= 0) return;

      el.dataset.eraspaceActualFps = FRAME_INTERVAL_LABEL;
      el.dataset.eraspaceActualSpeed = `${Math.round(getAutoSpeed() * 1000)}px/s`;

      setScrollLeft(el, latest, latest.position + delta * getAutoSpeed() * latest.direction, time);
      normalizeLoop(el, latest, time);
    };

    state.frame = window.requestAnimationFrame(step);
  };

  const init = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((el) => {
      removeCollectionCardOverlays(el);

      if (states.has(el)) return;

      const track = getTrack(el);
      if (!track) return;

      ensureDragStyles();
      disableNativeDrag(el);

      if (DESKTOP_QUERY.matches) {
        el.classList.add('eraspace-feed--drag-ready');
      }

      const visualDirection = getVisualDirection(el);
      el.dataset.eraspaceFeedDirection = visualDirection > 0 ? 'left' : 'right';

      const state = {
        visible: false,
        hoveredStrip: false,
        focusedSelection: false,
        dragCandidate: false,
        dragging: false,
        dragPointerId: null,
        dragPointerType: null,
        dragStartX: 0,
        dragStartY: 0,
        dragLastX: 0,
        dragLastTime: 0,
        dragVelocity: 0,
        blockClick: false,
        dragStartAt: 0,
        pointerInside: false,
        pointerX: 0,
        pointerY: 0,
        direction: visualDirection,
        pausedUntil: performance.now() + INITIAL_RESUME_DELAY,
        frame: 0,
        lastFrame: 0,
        inertiaFrame: 0,
        inertiaTimer: 0,
        inertiaLastFrame: 0,
        inertiaVelocity: 0,
        inertiaSource: '',
        wheelLastTime: 0,
        wheelVelocity: 0,
        ignoreScrollUntil: 0,
        autoScrolling: false,
        position: el.scrollLeft,
        lastWrittenScrollLeft: el.scrollLeft,
        track,
      };

      states.set(el, state);
      normalizeLoop(el, state, performance.now());

      el.addEventListener('pointerenter', (event) => syncPointer(el, event), { passive: true });
      el.addEventListener('pointermove', (event) => syncPointer(el, event), { passive: true });
      el.addEventListener('pointermove', (event) => handleDragMove(el, event), { passive: false });
      el.addEventListener('pointerdown', (event) => {
        if (isSelectionTarget(el, event.target) || isStripHoverTarget(el)) pause(el, INPUT_RESUME_DELAY);
        beginDrag(el, event);
      }, { passive: true });

      el.addEventListener('dragstart', (event) => {
        if (event.target instanceof Element && (event.target.closest('img, picture, a') || isSelectionTarget(el, event.target))) {
          event.preventDefault();
        }
      }, true);

      ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((eventName) => {
        el.addEventListener(eventName, (event) => endDrag(el, event), { passive: true });
      });

      el.addEventListener('click', (event) => {
        const latest = states.get(el);
        if (!latest || !latest.blockClick) return;

        latest.blockClick = false;
        event.preventDefault();
        event.stopPropagation();
      }, true);

      el.addEventListener('pointerleave', () => {
        state.pointerInside = false;
        state.hoveredStrip = false;
        state.lastFrame = 0;
      }, { passive: true });

      el.addEventListener('focusin', (event) => {
        state.focusedSelection = isSelectionTarget(el, event.target);
        state.lastFrame = 0;
      }, { passive: true });

      el.addEventListener('focusout', () => {
        state.focusedSelection = false;
        state.lastFrame = 0;
      }, { passive: true });

      ['touchstart', 'keydown'].forEach((eventName) => {
        el.addEventListener(eventName, (event) => {
          if (isSelectionTarget(el, event.target) || isStripHoverTarget(el)) pause(el, INPUT_RESUME_DELAY);
        }, { passive: true });
      });

      el.addEventListener('wheel', (event) => {
        if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;

        const latest = states.get(el);
        if (!latest || !latest.track || latest.track.scrollWidth <= el.clientWidth + 8) return;

        event.preventDefault();

        const time = performance.now();
        const modeScale = event.deltaMode === WheelEvent.DOM_DELTA_LINE ? 16 : event.deltaMode === WheelEvent.DOM_DELTA_PAGE ? el.clientWidth : 1;
        const wheelDelta = event.deltaX * modeScale * getWheelMultiplier();
        const direction = wheelDelta > 0 ? 1 : -1;
        const deltaTime = Math.max(8, time - (latest.wheelLastTime || time - 16));

        stopInertia(latest);
        latest.wheelLastTime = time;
        latest.wheelVelocity = clampVelocity(latest.wheelVelocity ? (latest.wheelVelocity * 0.45) + ((wheelDelta / deltaTime) * 0.55) : (wheelDelta / deltaTime));

        setScrollLeft(el, latest, latest.position + wheelDelta, time);
        normalizeDirection(el, latest, time, direction);
        latest.pausedUntil = Math.max(latest.pausedUntil, time + INPUT_RESUME_DELAY);
        latest.lastFrame = 0;
        startInertia(el, latest, latest.wheelVelocity, 'wheel', WHEEL_INERTIA_DELAY);
      }, { passive: false });

      el.addEventListener('scroll', () => {
        const latest = states.get(el);
        if (!latest || latest.autoScrolling) return;

        const current = el.scrollLeft;
        const isOwnScroll = performance.now() <= latest.ignoreScrollUntil && Math.abs(current - latest.lastWrittenScrollLeft) < 1;
        if (isOwnScroll) return;

        const direction = current > latest.position ? 1 : current < latest.position ? -1 : latest.direction;
        latest.position = current;
        normalizeDirection(el, latest, performance.now(), direction);
        pause(el, INPUT_RESUME_DELAY);
      }, { passive: true });

      start(el);
    });
  };

  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const state = states.get(entry.target);
      if (!state) return;

      state.visible = entry.isIntersecting;
      if (state.visible) start(entry.target);
    });
  }, { threshold: 0 });

  const observeNewFeeds = (root = document) => {
    root.querySelectorAll(SELECTOR).forEach((el) => observer.observe(el));
  };

  const boot = (root = document) => {
    init(root);
    observeNewFeeds(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => boot(event.target));
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) document.querySelectorAll(SELECTOR).forEach((el) => start(el));
  });
})();
